import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { KnowledgeArticleDetail, KnowledgeArticleSummary, KnowledgeCategory, PageResponse } from '../models';

interface ArticleData {
  slug: string;
  categoryCode: string | null;
  resourceType: string | null;
  trainingTrackCode: string | null;
  estimatedReadMinutes: number | null;
  tags: string | null;
  featured: boolean;
  mediaAssetId: string | null;
  documentId: string | null;
  body?: string;
}

/** Enveloppe générique SupportResponse côté backend : {id, type, title, status, data, ...}. */
interface SupportEnvelope<T> {
  id: string;
  title: string;
  data: T;
}

function toSummary(envelope: SupportEnvelope<ArticleData>): KnowledgeArticleSummary {
  return {
    id: envelope.id,
    title: envelope.title,
    slug: envelope.data.slug,
    categoryCode: envelope.data.categoryCode,
    resourceType: (envelope.data.resourceType ?? 'ARTICLE') as KnowledgeArticleSummary['resourceType'],
    trainingTrackCode: envelope.data.trainingTrackCode,
    estimatedReadMinutes: envelope.data.estimatedReadMinutes,
    tags: envelope.data.tags,
    featured: envelope.data.featured,
    mediaAssetId: envelope.data.mediaAssetId,
    documentId: envelope.data.documentId,
  };
}

function toDetail(envelope: SupportEnvelope<ArticleData>): KnowledgeArticleDetail {
  return { ...toSummary(envelope), body: envelope.data.body ?? '' };
}

/**
 * UC-24 : base de connaissance / documentation publique (support-service).
 *
 * `/public/knowledge-base/articles` et `/public/knowledge-base/search` exécutent exactement la
 * même requête côté backend (SupportKnowledgeController délègue les deux à
 * `service.articles(query, PUBLISHED, categoryCode, pageable)`) : un seul appel avec `query`
 * suffit, pas besoin de distinguer "parcourir" et "rechercher" côté frontend.
 *
 * Contrairement à content-service, ce endpoint filtre réellement sur `query` (LIKE sur titre et
 * corps) — voir SupportSpecifications#articles.
 */
@Injectable({ providedIn: 'root' })
export class KnowledgeBaseService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/support-service/api/v1/support`;

  publicArticles(options: { query?: string; categoryCode?: string; size?: number } = {}): Observable<PageResponse<KnowledgeArticleSummary>> {
    let params = new HttpParams().set('size', options.size ?? 50);
    if (options.query) params = params.set('query', options.query);
    if (options.categoryCode) params = params.set('categoryCode', options.categoryCode);
    return this.http
      .get<PageResponse<SupportEnvelope<ArticleData>>>(`${this.baseUrl}/public/knowledge-base/articles`, { params })
      .pipe(map((res) => ({ ...res, content: res.content.map(toSummary) })));
  }

  publicArticle(slug: string): Observable<KnowledgeArticleDetail> {
    return this.http
      .get<SupportEnvelope<ArticleData>>(`${this.baseUrl}/public/knowledge-base/articles/${slug}`)
      .pipe(map(toDetail));
  }

  publicCategories(): Observable<KnowledgeCategory[]> {
    return this.http.get<KnowledgeCategory[]>(`${this.baseUrl}/public/categories`);
  }
}
